import { renovarAcessoAgora } from '@/shared/lib/api-client';
import { clearPersistedQueryCache } from '@/shared/lib/query-cache';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { Socket } from 'socket.io-client';
import { marcarMotivo, rotuloDoAviso } from './motivo-da-busca';
import { instalarReconexao } from './reconexao';
import { criarRecuperacaoDeAcesso } from './recuperacao-de-acesso';

/**
 * Proxy do Socket.IO que delega tudo pra um Web Worker dedicado.
 * O socket REAL roda no Worker — fora do main thread, imune a tab throttling
 * do Chrome quando a aba está em background.
 *
 * API pública (`on`/`off`/`emit`) é compatível com `Socket` do socket.io-client.
 * Os hooks que já usam `socket.on(...)` etc. seguem funcionando sem mudança.
 */

// biome-ignore lint/suspicious/noExplicitAny: callbacks de socket.io aceitam any[]
type Listener = (...args: any[]) => void;

interface WorkerMessage {
  type: 'event' | 'connect_state';
  event?: string;
  // biome-ignore lint/suspicious/noExplicitAny: payloads heterogêneos
  args?: any[];
  state?: 'connected' | 'disconnected' | 'connect_error';
  error?: string;
}

class SocketProxy {
  private listeners = new Map<string, Set<Listener>>();
  private worker: Worker;

  constructor(url: string, token: string) {
    this.worker = new Worker(new URL('./socket.worker.ts', import.meta.url), {
      type: 'module',
      name: 'chatplus-socket-worker',
    });

    this.worker.addEventListener('message', (e: MessageEvent<WorkerMessage>) => {
      const msg = e.data;
      if (msg.type === 'event' && msg.event) {
        this.dispatch(msg.event, msg.args ?? []);
        return;
      }
      if (msg.type === 'connect_state') {
        // Re-emite como evento `connect`/`disconnect`/`connect_error` pros listeners locais
        // (mesmo nome dos eventos default do Socket.IO).
        if (msg.state === 'connected') this.dispatch('connect', []);
        else if (msg.state === 'disconnected') this.dispatch('disconnect', [msg.error]);
        else if (msg.state === 'connect_error')
          this.dispatch('connect_error', [new Error(msg.error)]);
      }
    });

    // Inicializa o socket dentro do worker
    this.worker.postMessage({ type: 'init', payload: { url, token } });
  }

  on(event: string, fn: Listener): this {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn);
    return this;
  }

  off(event: string, fn?: Listener): this {
    if (!fn) this.listeners.delete(event);
    else this.listeners.get(event)?.delete(fn);
    return this;
  }

  // biome-ignore lint/suspicious/noExplicitAny: socket.io emit aceita any[]
  emit(event: string, ...args: any[]): this {
    this.worker.postMessage({ type: 'emit', payload: { event, args } });
    return this;
  }

  refreshAuth(token: string): void {
    this.worker.postMessage({ type: 'refresh_auth', payload: { token } });
  }

  destroy(): void {
    this.worker.postMessage({ type: 'disconnect' });
    this.worker.terminate();
    this.listeners.clear();
  }

  // biome-ignore lint/suspicious/noExplicitAny: dispatch de eventos heterogêneos
  private dispatch(event: string, args: any[]): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    // TEMPORÁRIO (motivo-da-busca.ts): busca disparada pelos handlers abaixo
    // sai etiquetada com este aviso.
    marcarMotivo(rotuloDoAviso(event, args[0]));
    for (const fn of handlers) {
      try {
        fn(...args);
      } catch (err) {
        // Não derruba outros listeners se um falhar.
        console.error(`[socket] listener error for "${event}":`, err);
      }
    }
  }
}

let singleton: SocketProxy | null = null;
let presenceHeartbeat: ReturnType<typeof setInterval> | null = null;
let visibilityListener: (() => void) | null = null;

/**
 * Reporta presença pro backend igual o mobile faz. Quando aba está
 * visível, seta `presence:fg:{tenant}:{user}` (TTL 60s no Redis) via
 * `app.state foreground`. Quando perde foco/fecha, manda `background`
 * que limpa a chave.
 *
 * Importante: worker-mobile-push consulta essa chave antes de disparar
 * push pro app — se setada, skip push. Sem isso, atendente logado no
 * web recebe ping no celular ao mesmo tempo que vê a msg na tela = ruído.
 *
 * Heartbeat a cada 30s renova a chave enquanto a aba ficar visível
 * (TTL é 60s pra cobrir blip de rede + skew).
 */
function startPresenceReporting(socket: SocketProxy): void {
  const reportCurrent = (): void => {
    const visible = typeof document !== 'undefined' && document.visibilityState === 'visible';
    socket.emit('app.state', visible ? 'foreground' : 'background');
  };

  // Estado inicial
  reportCurrent();

  // Heartbeat: renova foreground a cada 30s enquanto visible.
  // (Se hidden, ainda emite background pra garantir state limpo no servidor.)
  presenceHeartbeat = setInterval(reportCurrent, 30_000);

  // Reage a mudanças de visibilidade da aba (foco/blur, switch tab, lock).
  if (typeof document !== 'undefined') {
    visibilityListener = reportCurrent;
    document.addEventListener('visibilitychange', visibilityListener);
  }
}

function stopPresenceReporting(): void {
  if (presenceHeartbeat) {
    clearInterval(presenceHeartbeat);
    presenceHeartbeat = null;
  }
  if (visibilityListener && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
}

/** Resolve URL do Socket.IO. Em dev/prod o servidor está na mesma origem da API. */
function resolveSocketUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  if (fromEnv && /^https?:/.test(fromEnv)) {
    // remove path /api/v1 — socket.io fica na raiz do host
    return fromEnv.replace(/\/api\/v\d+\/?$/, '');
  }
  // Default: same-origin (Vite proxy NÃO cobre WebSocket — em dev, se VITE_API_URL não setado, usa origin atual)
  return window.location.origin;
}

interface SessionRevokedPayload {
  tenant_id: number;
  user_id: number;
  reason: 'user_logout' | 'admin_revoked';
  timestamp: string;
}

/**
 * Retorna o socket singleton (proxy do Worker).
 *
 * O token novo é guardado quando muda (via `refreshSocketAuth`), mas a conexão
 * NÃO é derrubada por causa disso — ela só é refeita se já estiver fora do ar.
 * O porquê está no `refresh_auth` do `socket.worker.ts`.
 *
 * Tipo de retorno é `Socket` (do socket.io-client) pra compatibilidade
 * com os hooks que já existem — internamente é um SocketProxy implementando
 * o subset usado (on/off/emit). Cast `as unknown as Socket` é pragmático.
 */
export function getSocket(): Socket {
  if (singleton) return singleton as unknown as Socket;

  const token = useAuthStore.getState().accessToken;
  singleton = new SocketProxy(resolveSocketUrl(), token ?? '');

  // Recuperação do que se perdeu fora do ar. Registrado AQUI, na mesma volta
  // síncrona da construção, e não num `useEffect`: `SocketProxy.on()` não
  // repete evento passado, então um `connect` que chegasse antes do React
  // montar levaria a recuperação junto. Como a mensagem vem do Worker por
  // `postMessage`, ela só pode chegar no próximo ciclo — depois desta linha.
  instalarReconexao(singleton);

  // Ao conectar (e reconectar), re-emite presence pra repopular a chave
  // Redis. A cada reconnect novo socket session = chave precisa ser setada
  // de novo.
  singleton.on('connect', () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      singleton?.emit('app.state', 'foreground');
    }
  });

  // Recusa do servidor (crachá vencido/revogado) → renova pelo caminho do 401
  // (que reconecta o socket), com freio. Vide recuperacao-de-acesso.ts.
  const recuperacao = criarRecuperacaoDeAcesso({ renovarAcesso: renovarAcessoAgora });
  singleton.on('connect', () => recuperacao.aoConectar());
  singleton.on('connect_error', (err: Error) => recuperacao.aoRecusar(err.message));
  singleton.on('disconnect', (reason?: string) => recuperacao.aoCair(reason ?? ''));

  // Inicia reporte contínuo de presença (heartbeat + visibilitychange).
  startPresenceReporting(singleton);

  // Logout remoto: server revoga refresh tokens (admin ou outro device do
  // mesmo user clicou "Sair") e emite isso na room `tenant:X:user:Y`.
  // Frontend força reload total pra cair no /login. NÃO chamamos clearSession()
  // antes: o reload já descarta o estado, e clearSession() dispararia um
  // re-render com user=null que pisca o error boundary do router por ms.
  singleton.on('auth.session_revoked', (payload: SessionRevokedPayload) => {
    const reasonLabel =
      payload.reason === 'admin_revoked' ? 'admin' : 'logout em outro dispositivo';
    sessionStorage.setItem('logout_reason', reasonLabel);
    clearPersistedQueryCache();
    window.location.href = '/login';
  });

  // Admin alterou vínculos de dept do user — reload total. Cache de tickets
  // pode ter entradas que não devem mais ser visíveis (ou faltar novas),
  // rooms de realtime precisam ser re-joinadas. Reload garante consistência
  // sem complicar lógica de reconciliação client-side.
  singleton.on('user.departments_changed', () => {
    clearPersistedQueryCache();
    sessionStorage.setItem('reload_reason', 'departments_changed');
    window.location.reload();
  });

  // Admin mudou o perfil de acesso do user (ou as telas do perfil dele): busca
  // o acesso novo. Só GANHOU coisa → atualiza o menu sem reload (ninguém perde
  // o que estava digitando). PERDEU alguma tela/permissão → limpa o cache
  // salvo e recarrega, senão dados que ele não pode mais ver (tela que saiu,
  // conversa de colega da lista "Visualizar todos") ficavam na tela e no
  // localStorage. O servidor já barra desde a mudança.
  singleton.on('user.access_changed', () => {
    const antes = useAuthStore.getState().user?.acesso ?? null;
    void import('@/features/auth/api')
      .then(({ buscarMeuAcesso }) => buscarMeuAcesso())
      .then((depois) => {
        const perdeu =
          antes !== null &&
          (antes.telas.some((t) => !depois?.telas.includes(t)) ||
            (antes.verTodos && !depois?.verTodos));
        if (perdeu) {
          clearPersistedQueryCache();
          sessionStorage.setItem('reload_reason', 'access_changed');
          window.location.reload();
          return;
        }
        useAuthStore.getState().setAcesso(depois);
      })
      .catch(() => {
        // Falhou (rede/servidor): fica com o acesso antigo até o próximo
        // refresh — o servidor continua barrando o que não pode.
      });
  });

  return singleton as unknown as Socket;
}

export function disconnectSocket(): void {
  stopPresenceReporting();
  singleton?.destroy();
  singleton = null;
}

/** Re-emite com novo token (usar após refresh). */
export function refreshSocketAuth(): void {
  if (!singleton) return;
  const token = useAuthStore.getState().accessToken;
  singleton.refreshAuth(token ?? '');
}
